<template>
  <div class="wl-searching">
    <div class="wl-searching__container">
      <div class="wl-searching__header">
        <div class="wl-searching__mugshot">
          <img
            class="wl-searching__mugshot-image"
            :src="mugshotGif"
            alt="Searching Image"
            aria-hidden="true"
          />
          <img
            class="wl-searching__lock-icon"
            :src="lockImage"
            alt="Lock Icon"
            aria-hidden="true"
          />
        </div>
        <div class="wl-searching__header-text">
          <img
            class="wl-searching__magnify-icon"
            :src="magnifyImage"
            alt="Magnifying Glass Icon"
            aria-hidden="true"
            height="13px"
            width="13px"
          />
          <span class="wl-searching__header-text--bold">
            {{ cms.page.search_text }}
          </span>
          <span class="wl-searching__search-subject">
            {{ searchSubject }}
          </span>
        </div>
      </div>
      <div class="wl-searching__card">
        <div class="wl-searching__loading-bar">
          <div class="wl-searching__countdown"></div>
          <tz-loading-bar
            v-if="loadingDelay"
            :starting-position="0"
            :show-indicator="true"
            :delay="loadingDelay"
            easing="cubic-bezier(0.49, 1, 0.98, 0.47)"
            @tz-loading-bar-completed="sendToResults()"
          >
            <tz-markdown :content="timeRemainingText" />
          </tz-loading-bar>
        </div>
        <div class="wl-searching__result-includes">
          <div class="wl-searching__result-title">
            <div class="wl-searching__result-title-text">
              {{ cms.page.results_title }}
            </div>
            <div class="wl-searching__confidential">
              <img
                :src="lockIconWhite"
                alt="Confidential Icon"
                aria-hidden="true"
                width="10px"
                height="10px"
              />
              <span class="wl-searching__confidential-text">
                {{ cms.page.confidential_text }}
              </span>
            </div>
          </div>
          <div class="wl-searching__result-list">
            <wl-spinner-text-list
              v-if="hasResultItemsTiming"
              :key="index"
              v-for="(item, index) in resultItems"
              :index="index"
              :item="item"
              :check-color="cms.page.check_color"
              :spinner-text-array="resultItems"
              :spinner-color="cms.page.spinner_color"
              :spinner-stroke="cms.page.spinner_stroke"
            />
          </div>
        </div>
      </div>
    </div>
    <div
      class="wl-searching__overlay"
      :v-if="showModal"
      :class="showModal ? 'wl-searching__overlay--show' : ''"
    >
      <wl-modal-form
        ref="cityModal"
        :aria-hidden="!openModal.city"
        class="wl-searching__city-modal"
        :open="openModal.city"
        :close-modal="closeModal"
        :modal-title="cityModalTitle"
        :modal-text="cms.page.where_live_modal.subtitle"
        :inputs="cityInputs"
        :confirm-button-text="cms.page.where_live_modal.button_text_confirm"
        :should-disable-confirm="cityModalConfirmDisabled"
        :cancel-button-text="cms.page.where_live_modal.button_text_skip"
        @wl-modal-form-confirm="handleCity(true)"
        @wl-modal-form-cancel="handleCity(false)"
      />
      <wl-modal-form
        :aria-hidden="!openModal.age"
        class="wl-searching__button-modal"
        :open="openModal.age"
        :close-modal="closeModal"
        :modal-title="ageModalTitle"
        :inputs="ageInputs"
        :cancel-button-text="cms.page.age_range_modal.close_link_text"
        @wl-modal-form-cancel="handleAge(false)"
        @wl-modal-form-clicked="handleAge"
      />
      <wl-modal-form
        :aria-hidden="!openModal.questions"
        class="wl-searching__button-modal"
        :open="openModal.questions"
        :close-modal="handleQuestionClick"
        :modal-title="currentQuestionTitle"
        :inputs="questionInputs"
        :cancel-button-text="cms.page.questions_modal.skip_text"
        @wl-modal-form-cancel="handleQuestionClick"
        @wl-modal-form-clicked="handleQuestionClick"
      />
    </div>
  </div>
</template>

<script>
const pageCms = require(`@/assets/cms/pages/landing.json`);
// Components
import TzLoadingBar from '@trazi/tz-loading-bar/src/tz-loading-bar.vue';
import TzMarkdown from '@trazi/tz-markdown/src/tz-markdown.vue';
import WlModalForm from '@/components/shared/wl-modal-form.vue';
import WlSpinnerTextList from '@/components/shared/wl-spinner-text-list.vue';
// Images
import lockIconWhite from '@/assets/images/shared/icon-lock.svg';
import lockImage from '@/assets/images/shared/lock-icon-black.svg';
import mugshotGif from '@/assets/images/shared/mugshot.gif';
// Helpers
const NBars = require('nbars/commonjs.js');
import setTitleCase from '@/assets/js/shared/helpers/setTitleCase';
import parseParams from '@/assets/js/shared/helpers/parseParams';
import formatCity from '@/assets/js/shared/helpers/formatCity';
import getStatesForDropdown from '@/assets/js/shared/helpers/getStatesForDropdown';
import getStateAbbrFromName from '@/assets/js/shared/helpers/getStateAbbrFromName';
import setLandingSession from '@/assets/js/shared/helpers/setLandingSession';
import navigateHelper from '@/assets/js/shared/helpers/navigateHelper';

const categoryProps = {
  opacity: 0.3,
  setCheck: false,
  spinnerDelay: null,
  checkDelay: null,
  isStartingSpin: false
};
const resultItems = pageCms.searching.result_items.map(result => ({
  ...categoryProps,
  title: result.title
}));

export default {
  name: 'wl-searching',
  components: {
    TzLoadingBar,
    TzMarkdown,
    WlModalForm,
    WlSpinnerTextList
  },
  computed: {
    hasResultItemsTiming() {
      return resultItems[0].spinnerDelay !== null;
    },
    initialCountdownSeconds() {
      return parseInt(pageCms.searching.countdown[this.deviceType].timer);
    },
    deviceType() {
      return this.isMobile ? 'mobile' : 'desktop';
    },
    ageModalTitle() {
      return NBars.transform(this.cms.page.age_range_modal.title, {
        firstName: this.firstName
      });
    },
    cityModalTitle() {
      return NBars.transform(this.cms.page.where_live_modal.title, {
        firstName: this.firstName
      });
    },
    currentQuestionTitle() {
      return NBars.transform(
        this.cms.page.questions_modal.question_list[this.currentQuestion].title,
        {
          firstName: this.firstName
        }
      );
    },
    firstName() {
      return setTitleCase({ text: this.$route.query.firstName });
    },
    isLastQuestion() {
      return (
        this.currentQuestion + 1 >=
        this.cms.page.questions_modal.question_list.length
      );
    },
    lastName() {
      return parseParams({
        word: this.$route.query.lastName,
        splitBy: ['_'],
        parseBy: ' '
      });
    },
    magnifyImage() {
      if (this.cms.page.magnify_color) {
        return require(`@/assets/images/shared/icon-magnify-${this.cms.page.magnify_color.toLowerCase()}.svg`);
      }
      return require(`@/assets/images/shared/icon-magnify-pink.svg`);
    },
    showModal() {
      return Object.keys(this.openModal).some(k => this.openModal[k] === true);
    },
    timeRemainingText() {
      return NBars.transform(this.cms.page.time_remaining_label, {
        seconds: this.countdownSeconds
      });
    },
    searchSubject() {
      const formattedCity = this.city && formatCity({ city: this.city });
      const stateAbbr =
        this.state && getStateAbbrFromName({ state: this.state });
      let copy = `${this.firstName} ${this.lastName}`;
      if (this.city && !this.state) {
        copy += ` in ${formattedCity}`;
      } else if (!this.city && this.state) {
        copy += ` in ${stateAbbr}`;
      } else if (this.city && this.state) {
        copy += ` in ${formattedCity}, ${stateAbbr}`;
      }
      if (this.age) {
        const splitAge = this.age.split('-');
        const minAge = splitAge[0];
        const maxAge = splitAge[1];
        copy += `, ages: ${minAge} - ${maxAge}`;
      }
      return copy;
    }
  },
  data() {
    return {
      isMobile: false,
      age: this.$route.query.age,
      ageInputs: [
        {
          type: 'button',
          valueOptions: this.cms.page.age_range_modal.button_list
        }
      ],
      backgroundImage: '',
      city: this.$route.query.city,
      cityModalConfirmDisabled: false,
      cityInputs: [
        {
          name: 'city',
          type: 'text',
          defaultValue: '',
          optionalText: 'Optional',
          placeholder: 'City',
          validationType: 'text'
        },
        {
          name: 'state',
          type: 'select',
          defaultValue: this.$route.query.state || '',
          valueOptions: getStatesForDropdown({
            hasTerritories: true,
            useStateAbbrAsValue: true,
            includeAllStatesOption: true
          })
        }
      ],
      countdownSeconds: null,
      currentQuestion: 0,
      loadingDelay: null,
      lockIconWhite: lockIconWhite,
      lockImage: lockImage,
      mugshotGif: mugshotGif,
      questionInputs: [
        {
          type: 'button',
          valueOptions: this.cms.page.questions_modal.button_list
        }
      ],
      questionUserAnswers: [],
      resultItems,
      openModal: {
        age: false,
        city: false,
        questions: false
      },
      state: this.$route.query.state,
      hasSentToResults: false,
      altResultIds: [
        'aust04',
        'gr05eg',
        'capt06',
        '1ll1ca',
        'c4l1',
        '1ll1rp',
        'r1t3s',
        '3lv1s',
        'h4rm0',
        '81llv2',
        'r0b3rt',
        'r0bpr0g',
        'n0f0rm',
        '8170',
        'al22',
        'r0bf0rm',
        'c0p1',
        'c0p2'
      ]
    };
  },
  methods: {
    closeModal() {
      return null;
    },
    handleAge(buttonValue) {
      if (buttonValue) {
        this.age = buttonValue;
      }
      this.openModal.age = false;
      // Allow Age Modal to close/scrim to disappear before opening questions modal
      setTimeout(() => {
        this.openModal.questions = true;
      }, 300);
    },
    handleCity(shouldSubmit = false) {
      const city = this.cityInputs[0].defaultValue;
      const state = this.cityInputs[1].defaultValue;

      // Only set city and state if state is provided (sometimes city can be inputted without state))
      if (shouldSubmit && state) {
        this.city = city;
        this.state = state;
      }
      this.openModal.city = false;
      if (this.$route.query.hasOwnProperty('age')) {
        this.openModal.questions = true;
      } else {
        this.openModal.age = true;
      }
    },
    handleQuestionClick(value) {
      // Tracking the answers in case we ever use them
      this.questionUserAnswers.push(value || null);
      // If this is the last question, close modal
      if (this.isLastQuestion) {
        return (this.openModal.questions = false);
      }
      // Increment question otherwise
      return this.currentQuestion++;
    },
    onResizeWidth() {
      const windowSize = window.innerWidth;
      this.isMobile = windowSize < 992;
    },
    sendToResults() {
      if (this.hasSentToResults) {
        return;
      }
      const query = this.$route.query;
      query.city = this.city || '';
      query.state = this.state || '';
      query.age = this.age || '';

      const landingId = this.$route.params.landingID;

      // Determine result page version depending on the alt result id's
      const resultUrl = this.altResultIds.includes(landingId)
        ? pageCms.steps.results_url_alt
        : pageCms.steps.results_url;

      const landingUrl = NBars.transform(resultUrl, {
        landingId
      });

      setLandingSession(query);
      setTimeout(() => {
        navigateHelper({ navigateUrl: landingUrl, query: query });
      }, 1000);

      this.hasSentToResults = true;
    },
    setSessionStorage() {
      setLandingSession(this.$route.query);
    },
    startSpinners() {
      const seconds = this.initialCountdownSeconds / this.resultItems.length;
      const delay = this.$route.query.testing ? seconds * 50 : seconds * 1000;
      for (let i = 0; i < this.resultItems.length; i++) {
        this.resultItems[i].spinnerDelay = i * delay;
        this.resultItems[i].checkDelay = (i + 1) * delay;
      }
    },
    setTimeRemainingInterval() {
      this.countdownInterval = setInterval(() => {
        this.countdownSeconds--;
        if (this.countdownSeconds <= 0) {
          clearInterval(this.countdownInterval);
        }
      }, 1000);
    }
  },
  beforeDestroy() {
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
    }
  },
  async mounted() {
    this.onResizeWidth();

    this.countdownSeconds = this.initialCountdownSeconds;
    this.loadingDelay = this.$route.query.testing
      ? 15
      : this.initialCountdownSeconds * 10;

    if (!this.cityInputs[1].defaultValue.length) {
      this.cityInputs[1].defaultValue = this.$store.getters['location/state'];
    }

    this.setSessionStorage();

    this.startSpinners();

    this.setTimeRemainingInterval();

    setTimeout(() => {
      if (!this.city) {
        this.openModal.city = true;
      } else if (!this.age) {
        this.openModal.age = true;
      } else {
        this.openModal.questions = true;
      }

      // Watch city modal inputs to update confirm button visibility
      this.$refs?.cityModal?.$watch(
        'inputs',
        newVal => {
          const city = newVal[0]?.defaultValue;
          const state = newVal[1]?.defaultValue;

          if (city && !state) {
            this.cityModalConfirmDisabled = true;
          } else {
            this.cityModalConfirmDisabled = false;
          }
        },
        {
          deep: true
        }
      );
    }, 2500);
  },
  head() {
    const transformedMeta = [];
    const metaTitle = pageCms.searching.page_title;
    const metaContent = pageCms.searching.meta_root;
    const title = NBars.transform(metaTitle, {
      searchSubject: this.searchSubject
    });

    for (let i = 0; i < metaContent.length; i++) {
      const metaCopy = Object.assign({}, metaContent[i]);
      const { hid, content } = metaCopy;

      switch (hid) {
        case 'og:title':
        case 'apple-mobile-web-app-title':
          metaCopy.content = NBars.transform(content, {
            searchSubject: this.searchSubject
          });
      }

      transformedMeta[i] = metaCopy;
    }

    return {
      title: title,
      meta: transformedMeta
    };
  },
  props: {
    cms: {
      type: Object,
      default: () => {
        return {
          page: pageCms.searching
        };
      }
    }
  }
};
</script>
